<template>
  <!-- Header needs to blend in with the homepage on XS2Event branded shops -->
  <div
    :class="[
      'nav-header  w-full z-30 bg-header',
      // The event page (the only one without the main menu) has a plain border instead of the shadow. Desktop
      // only: on mobile its search bar and event bar follow straight on, under the rule inside.
      hideMainMenu ? 'lg:border-b border-border' : 'shadow-sm',
      { ' ': !isXS2EventShop || route.name != 'home' || !lg },
    ]">
    <Disclosure id="nav" ref="disclosure" as="nav" class="lg:h-auto" :class="fullWidth ? 'px-5' : 'page-container'">
      <div class="py-2.5 grid gap-2">
        <!-- First Row: Logo on the Left, Right Elements on the Right -->
        <!-- Desktop is a three-track grid: equal `1fr` side columns put the search on the page's centre
             line, and neither goes below its full (max-content) width, so as the screen narrows it
             is the search track, up to 45rem, that shrinks rather than running over the logo or the
             menus. -->
        <div
          class="relative flex justify-between items-center gap-3 lg:grid lg:grid-cols-[minmax(max-content,1fr)_minmax(0,45rem)_minmax(max-content,1fr)]">
          <!-- Mobile: equal-width side columns, flex-1 from a zero basis. -->
          <div class="flex flex-1 basis-0 items-center gap-1">
            <!-- Mobile Menu Button (Hamburger) -->
            <div class="inset-y-0 left-0 flex items-center lg:hidden">
              <DisclosureButton
                class="flex size-11 items-center justify-center outline-none rounded-md text-menu-link"
                @click="isMenuOpen = true">
                <span class="sr-only">{{ $t("Open main menu") }}</span>
                <Bars3Icon class="block h-6 w-6" aria-label="Menu" aria-hidden="true" />
              </DisclosureButton>
            </div>

            <!-- Mobile Logo: the brand icon in a 44px target, like the menu buttons beside it. Brands
                 without an icon keep their mobile logo. -->
            <div class="flex lg:hidden">
              <RouterLink to="/" class="flex size-11 items-center justify-center">
                <img v-if="icon" class="h-6 w-auto" alt="logo" :src="icon" />
                <img v-else class="mobile-logo size-6 object-contain" alt="logo" :src="logo_mobile" />
              </RouterLink>
            </div>

            <!-- Desktop Logo -->
            <div class="hidden lg:flex flex-shrink-0 items-center">
              <RouterLink to="/"><img class="logo max-h-8" :src="logo" alt="logo" /></RouterLink>
            </div>
          </div>

          <div class="hidden lg:flex min-w-0 justify-center">
            <SearchMenu class="w-full" />
          </div>

          <Alert v-if="cart.allocationId" status="warning" class="lg:col-span-3">
            {{ $t("Please beware! Regular bookings are disabled during your allocation checkout.") }}
            <button class="underline" @click="cart.emptyCart()">{{ $t("Clear cart") }}</button>
          </Alert>

          <!-- Right Elements -->
          <div class="flex flex-1 basis-0 justify-end items-center gap-2 lg:col-start-3 lg:row-start-1">
            <BrandMenu v-if="isLocal || (isTest && (isXS2Event || brandOverride))" @click="isMenuOpen = false" />
            <UserMenu @click="isMenuOpen = false" @open-impersonation="isImpersonationOpen = true" />
            <CartMenu v-if="!isB2C" data-testid="shopping-cart-menu" />
          </div>
        </div>
        <!-- Without the main menu, the rule only divides the icons from the mobile search bar below. -->
        <hr class="bg-border mt-0.5" :class="{ 'lg:hidden': hideMainMenu }" />
        <!-- Second Row: Left Elements (Main Menu) -->
        <div v-if="!hideMainMenu" class="hidden lg:flex justify-start items-center mt-2 lg:mt-0 py-2">
          <MainMenu />
        </div>
      </div>

      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="-translate-y-4 opacity-0"
        enter-to-class="translate-y-0 opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="translate-y-0 opacity-100"
        leave-to-class="-translate-y-4 opacity-0">
        <DisclosurePanel v-if="isMenuOpen" static class="lg:hidden z-20">
          <MainMenu :open="isMenuOpen" @close="isMenuOpen = false" />
        </DisclosurePanel>
      </transition>
    </Disclosure>
  </div>

  <!-- Mobile Search Bar -->
  <div
    class="flex lg:hidden bg-header w-full page-container pb-2.5"
    :class="{ 'border-b shadow border-solid border-white': !hideMainMenu }">
    <SearchMenu class="w-full" />
  </div>

  <XS2EventLogoBackground v-if="isXS2EventShop && route.name == 'home'" />
</template>

<script setup lang="ts">
import { SearchMenu, UserMenu, CartMenu } from "./menu";
import MainMenu from "./menu/MainMenu.vue";
import { ref, watch } from "vue";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/vue";
import { Bars3Icon } from "@heroicons/vue/24/outline";
import brandconfig, { isB2C, isXS2EventShop } from "@/brandconfig";
import { useCartStore } from "@/store/cart";
import BrandMenu from "./menu/BrandMenu.vue";
import { useRoute } from "vue-router";
import { isLocal, isTest } from "@/config/index.js";
import { Alert } from "@xs2event/js-lib/components";
import { useUserStore } from "@/store/user.js";
import XS2EventLogoBackground from "./XS2EventLogoBackground.vue";
import { useBreakpoints } from "@xs2event/js-lib/util";
import { useLocalStorage } from "@vueuse/core";

// The event page leaves out the main menu row (it has its own header row underneath) and spans the
// full width, like the page below it.
const { hideMainMenu = false, fullWidth = false } = defineProps<{ hideMainMenu?: boolean; fullWidth?: boolean }>();

const userStore = useUserStore();

const { lg } = useBreakpoints();

const isMenuOpen = ref(false);

const route = useRoute();

watch(route, () => (isMenuOpen.value = false));

const cart = useCartStore();

const isImpersonationOpen = ref(false);

const brandOverride = useLocalStorage("b2c_override");

const logo = brandconfig.LOGO;
const logo_mobile = brandconfig.LOGO_MOBILE;
const icon = brandconfig.BRAND_ICON;

const isXS2Event = ["9fd5d80db8c548208f1abd44c9b6c5e4_dst", "9055d412ef94473b9ae16c7b15db6141_dst"].includes(
  userStore.distributor_id
);
</script>

<style scoped>
.logo {
  height: 30px;
}
</style>
